const kayenMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
document.documentElement.classList.add('kayen-reveal-ready');

function kayenUpdateHeroVideo(hero) {
  if (hero.dataset.kayenCampaignReady === 'true') return;
  const frames = [...hero.querySelectorAll('[data-kayen-campaign-frame]')];
  if (frames.length < 2) return;
  hero.dataset.kayenCampaignReady = 'true';

  let activeIndex = 0;
  let timer;
  const hydrate = (frame) => {
    const image = frame?.querySelector('img[data-src]');
    const loadedImage = image || frame?.querySelector('img');
    if (!loadedImage) return Promise.resolve(false);
    if (!image && loadedImage.complete && loadedImage.naturalWidth) return Promise.resolve(true);
    if (!image) {
      return new Promise((resolve) => {
        loadedImage.addEventListener('load', () => resolve(true), { once: true });
        loadedImage.addEventListener('error', () => resolve(false), { once: true });
      });
    }
    image.src = image.dataset.src;
    image.removeAttribute('data-src');
    return new Promise((resolve) => {
      if (image.complete) return resolve(Boolean(image.naturalWidth));
      image.addEventListener('load', () => resolve(true), { once: true });
      image.addEventListener('error', () => resolve(false), { once: true });
    });
  };
  const stop = () => window.clearInterval(timer);
  let transitioning = false;
  const advance = async () => {
    if (transitioning) return;
    transitioning = true;
    const nextIndex = (activeIndex + 1) % frames.length;
    const ready = await hydrate(frames[nextIndex]);
    if (ready) {
      frames[activeIndex].classList.remove('is-active');
      frames[nextIndex].classList.add('is-active');
      activeIndex = nextIndex;
      hydrate(frames[(activeIndex + 1) % frames.length]);
    }
    transitioning = false;
  };
  const start = () => {
    stop();
    if (kayenMotionQuery.matches || document.hidden) return;
    timer = window.setInterval(advance, 5800);
  };

  hydrate(frames[1]);
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting ? start() : stop(), { threshold: 0.05 });
    observer.observe(hero);
  } else {
    start();
  }
  document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
}

function kayenInitHero(root = document) {
  root.querySelectorAll('[data-kayen-hero]').forEach(kayenUpdateHeroVideo);
}

let kayenRevealObserver;

function kayenInitReveals(root = document) {
  const revealItems = root.querySelectorAll('.kayen-reveal');

  if (!revealItems.length) return;

  if (kayenMotionQuery.matches || !('IntersectionObserver' in window)) {
    revealItems.forEach((item) => item.classList.add('kayen-reveal--visible'));
    return;
  }

  if (!kayenRevealObserver) {
    kayenRevealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('kayen-reveal--visible');
          kayenRevealObserver.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.15,
      }
    );
  }

  revealItems.forEach((item) => kayenRevealObserver.observe(item));
}

function kayenOrbitProgress(orbit) {
  if (orbit.dataset.kayenOrbitVisible === 'false') return;
  const rect = orbit.getBoundingClientRect();
  const travel = Math.max(1, orbit.offsetHeight - window.innerHeight);
  const progress = Math.min(1, Math.max(0, -rect.top / travel));
  orbit.style.setProperty('--orbit-progress', progress.toFixed(4));
  orbit.style.setProperty('--orbit-progress-percent', `${(progress * 100).toFixed(2)}%`);
  orbit.style.setProperty('--orbit-shift-x', `${((progress - 0.5) * 8).toFixed(2)}vw`);
  orbit.style.setProperty('--orbit-shift-y', `${((0.5 - progress) * 9).toFixed(2)}vh`);
  orbit.style.setProperty('--orbit-turn', `${((progress - 0.5) * 42).toFixed(2)}deg`);
  orbit.style.setProperty('--orbit-tilt', `${((progress - 0.45) * -14).toFixed(2)}deg`);
  orbit.style.setProperty('--orbit-roll', `${((progress - 0.5) * -4).toFixed(2)}deg`);
  orbit.style.setProperty('--orbit-scale', (0.82 + progress * 0.22).toFixed(3));
  orbit.style.setProperty('--orbit-spin', `${(progress * 160).toFixed(2)}deg`);
  orbit.style.setProperty('--orbit-counter-spin', `${(progress * -130).toFixed(2)}deg`);
  orbit.style.setProperty('--orbit-glow-x', `${((progress - 0.5) * 35).toFixed(2)}vw`);
  orbit.style.setProperty('--orbit-glow-reverse-x', `${((0.5 - progress) * 42).toFixed(2)}vw`);
  orbit.style.setProperty('--orbit-glow-y', `${((progress - 0.5) * 50).toFixed(2)}vh`);
  orbit.style.setProperty('--orbit-glow-opacity', (0.08 + progress * 0.16).toFixed(3));
  orbit.style.setProperty('--orbit-ring-opacity', (0.22 + progress * 0.45).toFixed(3));
  orbit.style.setProperty('--orbit-specular-x', `${((progress - 0.5) * 25).toFixed(2)}%`);
  orbit.style.setProperty('--orbit-shadow-scale', (1.25 - progress * 0.25).toFixed(3));
  orbit.style.setProperty('--orbit-intro-opacity', Math.max(0, 1 - progress * 4).toFixed(3));
  orbit.style.setProperty('--orbit-outro-opacity', Math.max(0, Math.min(1, (progress - 0.58) * 4)).toFixed(3));
  orbit.style.setProperty('--orbit-intro-y', `${(progress * -45).toFixed(1)}px`);
  orbit.style.setProperty('--orbit-outro-y', `${((1 - progress) * 45).toFixed(1)}px`);
  orbit.style.setProperty('--orbit-gesture-opacity', Math.max(0, Math.min(0.65, 0.65 - progress * 3.25)).toFixed(3));

  const video = orbit.querySelector('[data-kayen-orbit-video]');
  if (video?.dataset.kayenVideoReady === 'true' && Number.isFinite(video.duration)) {
    const targetTime = Math.min(video.duration - 0.04, Math.max(0, progress * (video.duration - 0.04)));
    if (Math.abs(video.currentTime - targetTime) > 0.035) video.currentTime = targetTime;
  }
}

let kayenOrbitFrame = 0;
function kayenUpdateOrbits() {
  if (kayenOrbitFrame) return;
  kayenOrbitFrame = requestAnimationFrame(() => {
    kayenOrbitFrame = 0;
    document.querySelectorAll('[data-kayen-orbit]').forEach(kayenOrbitProgress);
  });
}

function kayenInitOrbits(root = document) {
  root.querySelectorAll('[data-kayen-orbit]').forEach((orbit) => {
    kayenOrbitProgress(orbit);
    if (orbit.dataset.kayenOrbitReady === 'true') return;
    orbit.dataset.kayenOrbitReady = 'true';

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          orbit.dataset.kayenOrbitVisible = String(entry.isIntersecting);
          if (entry.isIntersecting) {
            const video = orbit.querySelector('[data-kayen-orbit-video]');
            const constrainedDevice = document.documentElement.classList.contains('kayen-lite-motion');
            if (video && !constrainedDevice && video.dataset.kayenVideoReady !== 'true') {
              video.querySelectorAll('source[data-src]').forEach((source) => {
                source.src = source.dataset.src;
                source.removeAttribute('data-src');
              });
              video.dataset.kayenVideoReady = 'true';
              video.load();
              video.addEventListener('loadedmetadata', kayenUpdateOrbits, { once: true });
            }
            kayenUpdateOrbits();
          }
        },
        { rootMargin: '100px 0px' }
      );
      visibilityObserver.observe(orbit);
    }

    if (!kayenMotionQuery.matches && window.matchMedia('(pointer: fine)').matches) {
      orbit.addEventListener('pointermove', (event) => {
        const rect = orbit.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / Math.min(rect.height, window.innerHeight) - 0.5;
        orbit.style.setProperty('--orbit-ry', `${(x * 10).toFixed(2)}deg`);
        orbit.style.setProperty('--orbit-rx', `${(y * -7).toFixed(2)}deg`);
      });
      orbit.addEventListener('pointerleave', () => {
        orbit.style.setProperty('--orbit-ry', '0deg');
        orbit.style.setProperty('--orbit-rx', '0deg');
      });
    }
  });
}

function kayenInitFilms(root = document) {
  root.querySelectorAll('[data-kayen-film]').forEach((film) => {
    if (film.dataset.kayenFilmReady === 'true') return;
    film.dataset.kayenFilmReady = 'true';
    const video = film.querySelector('[data-kayen-film-video]');
    if (!video) return;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const constrainedDevice = connection?.saveData || (navigator.deviceMemory && navigator.deviceMemory <= 2);
    if (kayenMotionQuery.matches || constrainedDevice) {
      film.classList.add('kayen-film--static');
      return;
    }

    const playFilm = () => {
      if (video.dataset.kayenVideoReady !== 'true') {
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.dataset.kayenVideoReady = 'true';
        video.load();
      }
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
      film.classList.add('is-playing');
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (film.dataset.kayenFilmPlayed !== 'true') {
          film.dataset.kayenFilmPlayed = 'true';
          playFilm();
        } else {
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
    }, { rootMargin: '80px 0px', threshold: 0.28 });
    observer.observe(film);
  });
}

function kayenApplyPerformanceProfile() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const constrainedDevice = connection?.saveData || (navigator.deviceMemory && navigator.deviceMemory <= 2);
  if (constrainedDevice) document.documentElement.classList.add('kayen-lite-motion');
}

function kayenInitProductDepth(root = document) {
  root.querySelectorAll("main[data-template^='product'] .product-information__media").forEach((media) => {
    if (media.dataset.kayenDepthReady === 'true') return;
    media.dataset.kayenDepthReady = 'true';
    media.classList.add('kayen-product-3d');

    const frame = media.querySelector('slideshow-container');
    if (frame && !frame.querySelector('.kayen-product-depth-label')) {
      const label = document.createElement('span');
      label.className = 'kayen-product-depth-label';
      label.textContent = window.matchMedia('(pointer: fine)').matches ? 'Move to explore' : 'Swipe to explore';
      frame.append(label);
    }

    const reset = () => {
      media.classList.remove('is-interacting');
      media.style.setProperty('--kayen-product-rx', '0deg');
      media.style.setProperty('--kayen-product-ry', '0deg');
      media.style.setProperty('--kayen-product-rx-n', '0');
      media.style.setProperty('--kayen-product-ry-n', '0');
      media.style.setProperty('--kayen-product-scale', '1');
      media.style.setProperty('--kayen-product-shadow-x', '0px');
      media.style.setProperty('--kayen-product-shadow-y', '22px');
    };

    media.addEventListener('pointermove', (event) => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const rect = media.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
      const y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
      media.classList.add('is-interacting');
      media.style.setProperty('--kayen-product-rx', `${(y * -3.8).toFixed(2)}deg`);
      media.style.setProperty('--kayen-product-ry', `${(x * 5.5).toFixed(2)}deg`);
      media.style.setProperty('--kayen-product-rx-n', (y * -3.8).toFixed(2));
      media.style.setProperty('--kayen-product-ry-n', (x * 5.5).toFixed(2));
      media.style.setProperty('--kayen-product-scale', '1.012');
      media.style.setProperty('--kayen-product-shadow-x', `${(x * -5.5).toFixed(2)}px`);
      media.style.setProperty('--kayen-product-shadow-y', `${(22 + y * -3.8).toFixed(2)}px`);
    });
    media.addEventListener('pointerleave', reset);
  });
}

function kayenUpdateInstagramDots(controls) {
  const nativeDots = [...controls.querySelectorAll('.slideshow-controls__dots .slideshow-control')];
  const indicator = controls.querySelector('.kayen-instagram-dots');

  if (!nativeDots.length || !indicator) return;

  const activeIndex = Math.max(0, nativeDots.findIndex((dot) => dot.getAttribute('aria-selected') === 'true'));
  const total = nativeDots.length;
  const start = Math.max(0, Math.min(activeIndex - 2, total - 5));
  const activeSlot = Math.min(4, Math.max(0, activeIndex - start));

  [...indicator.children].forEach((dot, index) => {
    dot.classList.toggle('is-active', index === activeSlot);
    dot.classList.toggle('is-edge', (index === 0 && start > 0) || (index === 4 && start + 5 < total));
  });
}

function kayenInitInstagramDots(root = document) {
  root.querySelectorAll('.media-gallery__mobile-controls').forEach((controls) => {
    if (controls.dataset.kayenDotsReady === 'true') return;

    const nativeDots = controls.querySelector('.slideshow-controls__dots');
    if (!nativeDots) return;

    const indicator = document.createElement('span');
    indicator.className = 'kayen-instagram-dots';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.innerHTML = '<i></i><i></i><i></i><i></i><i></i>';
    controls.append(indicator);
    controls.classList.add('kayen-instagram-pagination');
    controls.dataset.kayenDotsReady = 'true';

    const observer = new MutationObserver(() => kayenUpdateInstagramDots(controls));
    observer.observe(nativeDots, { attributes: true, subtree: true, attributeFilter: ['aria-selected'] });
    kayenUpdateInstagramDots(controls);
  });
}

let kayenDotsRefreshQueued = false;

function kayenQueueInstagramDotsRefresh() {
  if (kayenDotsRefreshQueued) return;

  kayenDotsRefreshQueued = true;
  requestAnimationFrame(() => {
    kayenDotsRefreshQueued = false;
    kayenInitInstagramDots();
  });
}

kayenApplyPerformanceProfile();
kayenInitHero();
kayenInitReveals();
kayenInitInstagramDots();
kayenInitOrbits();
kayenInitFilms();
kayenInitProductDepth();

window.addEventListener('scroll', kayenUpdateOrbits, { passive: true });
window.addEventListener('resize', kayenUpdateOrbits, { passive: true });

const kayenGalleryObserver = new MutationObserver((mutations) => {
  const galleryChanged = mutations.some((mutation) =>
    [...mutation.addedNodes].some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.('media-gallery, slideshow-controls, .media-gallery__mobile-controls') ||
          node.querySelector?.('media-gallery, slideshow-controls, .media-gallery__mobile-controls'))
    )
  );

  if (galleryChanged) kayenQueueInstagramDotsRefresh();
});

kayenGalleryObserver.observe(document.body, { childList: true, subtree: true });

document.addEventListener('change', (event) => {
  if (event.target.closest?.('variant-picker, .variant-picker')) {
    kayenQueueInstagramDotsRefresh();
  }
});

document.addEventListener('shopify:section:load', (event) => {
  kayenInitHero(event.target);
  kayenInitReveals(event.target);
  kayenInitInstagramDots(event.target);
  kayenInitOrbits(event.target);
  kayenInitFilms(event.target);
  kayenInitProductDepth(event.target);
});

const kayenMotionListener = () => {
  kayenInitHero();
  kayenInitReveals();
};

if (kayenMotionQuery.addEventListener) {
  kayenMotionQuery.addEventListener('change', kayenMotionListener);
} else if (kayenMotionQuery.addListener) {
  kayenMotionQuery.addListener(kayenMotionListener);
}
